import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API_BASE } from '@/context/AuthContext';
import { 
  ShieldCheck, 
  User, 
  Lock, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  ArrowRight, 
  KeyRound, 
  Building2,
} from 'lucide-react';
import axios from 'axios';

// Role → landing page (final spec §7)
const ROLE_LANDING = {
  admin: '/admin/dashboard',
  reception: '/staff/home',
  lab_staff: '/staff/referrals',
  pharmacist: '/staff/referrals',
  accountant: '/staff/referrals?payment_status=pending',  // Payment Clearance Workspace
  management: '/manager/calculations',                    // Legacy management role — now can do calculations
  manager: '/manager/calculations',                        // Manager referral calculation workspace
  marketing_executive: '/marketing/dashboard',              // Marketing exec sees their own referrals + reports
  staff: '/staff/home',
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user, loading: authLoading } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [systemStatus, setSystemStatus] = useState({ has_admin: true, loading: true });

  // Redirect if already authenticated. Force-change users always go to the change-password screen.
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      if (user?.must_change_password) {
        navigate('/profile/change-password?forced=1', { replace: true });
        return;
      }
      const from = location.state?.from?.pathname || ROLE_LANDING[user?.role] || '/staff/home';
      navigate(from, { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate, location]);

  // Check system status for admin existence
  useEffect(() => {
    const checkSystem = async () => {
      try {
        const res = await axios.get(`${API_BASE}/auth/system-status`);
        setSystemStatus({ has_admin: res.data.has_admin, loading: false });
      } catch (err) {
        setSystemStatus({ has_admin: true, loading: false });
      }
    };
    checkSystem();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('Please enter your username or staff name.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setIsSubmitting(true);
    const result = await login(username, password);
    setIsSubmitting(false);

    if (result.success) {
      // Force-change flow (spec §6)
      if (result.user?.must_change_password) {
        navigate('/profile/change-password?forced=1', { replace: true });
        return;
      }
      const from = location.state?.from?.pathname || ROLE_LANDING[result.user?.role] || '/staff/home';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-between relative overflow-hidden font-sans text-slate-100 selection:bg-teal-500 selection:text-white">
      {/* Background visual accents */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />

      {/* Header bar */}
      <header className="w-full max-w-7xl mx-auto px-6 py-6 flex items-center justify-between z-10">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-teal-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-teal-500/20">
            <Building2 className="w-6 h-6 text-slate-950 stroke-[2.5]" />
          </div>
          <div>
            <span className="text-xl font-bold tracking-tight text-white flex items-center gap-1.5 font-mono">
              <span className="text-teal-400 font-sans font-medium text-xs px-2 py-0.5 rounded-md bg-teal-950/80 border border-teal-800/60">RMS</span>
            </span>
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-400 bg-slate-900/60 border border-slate-800 px-3 py-1.5 rounded-full">
          <ShieldCheck className="w-4 h-4 text-teal-400" />
          <span>Encrypted Multi-User Authentication</span>
        </div>
      </header>

      {/* Main Login Card */}
      <main className="flex-1 flex items-center justify-center px-4 py-8 z-10">
        <div className="w-full max-w-md">
          <div className="bg-slate-900/90 border border-slate-800/80 rounded-2xl shadow-2xl backdrop-blur-xl p-8 sm:p-10 transition-all duration-200 hover:border-slate-700/80">
            
            {/* Header Content */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400 mb-4">
                <KeyRound className="w-6 h-6" />
              </div>
              <h1 data-testid="login-page-title" className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                Staff Login
              </h1>
              <p className="text-slate-400 text-sm mt-2">
                Sign in to your RMS account
              </p>
            </div>

            {/* Error Message Box */}
            {error && (
              <div 
                data-testid="login-error-alert" 
                className="mb-6 p-4 rounded-xl bg-red-950/50 border border-red-800/60 flex items-start space-x-3 text-red-200 text-sm animate-in fade-in slide-in-from-top-2 duration-200"
              >
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div className="flex-1 leading-relaxed">{error}</div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <label 
                  htmlFor="username" 
                  className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2"
                >
                  Username / Staff Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <User className="w-5 h-5" />
                  </div>
                  <input
                    id="username"
                    data-testid="staff-username-input"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter staff username"
                    autoComplete="username"
                    autoFocus
                    required
                    className="w-full pl-11 pr-4 py-3 bg-slate-950/60 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-colors text-sm"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label 
                    htmlFor="password" 
                    className="block text-xs font-semibold text-slate-300 uppercase tracking-wider"
                  >
                    Password
                  </label>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    id="password"
                    data-testid="staff-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter secure password"
                    autoComplete="current-password"
                    required
                    className="w-full pl-11 pr-11 py-3 bg-slate-950/60 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-colors text-sm"
                  />
                  <button
                    type="button"
                    data-testid="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-200 transition-colors focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                data-testid="staff-login-submit-btn"
                disabled={isSubmitting}
                className="w-full mt-2 py-3.5 px-4 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-semibold rounded-xl shadow-lg shadow-teal-500/25 transition-all duration-150 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isSubmitting ? (
                  <span>Authenticating...</span>
                ) : (
                  <>
                    <span>Sign In to Staff Workspace</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>

            {/* Forgot Password + Admin Recovery (spec §1 & §8) */}
            <div className="mt-4 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                data-testid="forgot-password-link"
                className="text-slate-400 hover:text-teal-300 transition-colors"
              >
                Forgot Password?
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin-recovery')}
                data-testid="admin-recovery-link"
                className="text-slate-500 hover:text-teal-300 transition-colors"
              >
                Admin account recovery
              </button>
            </div>

            {/* First-Time Setup Link if no admin exists */}
            {!systemStatus.loading && !systemStatus.has_admin && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  data-testid="setup-admin-link"
                  onClick={() => navigate('/setup')}
                  className="text-xs text-teal-400 hover:text-teal-300 font-medium underline underline-offset-4 transition-colors"
                >
                  First-Time System Initialization? Create Initial Administrator Account
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full max-w-7xl mx-auto px-6 py-6 text-center text-xs text-slate-500 z-10">
        <p>© 2026 RMS. Secure Staff Authentication Module.</p>
      </footer>
    </div>
  );
}
